import React, { useMemo } from 'react';
import { useGate } from 'statsig-react';
import { twMerge } from 'tailwind-merge';
import { gql } from '@soundxyz/gql-string';
import type { PostSource, ShareModalSource } from '@soundxyz/utils/analytics';
import { ERROR_TYPE, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { FeedPostReplyModal } from '@components/modals/FeedPostCommentModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  ActionButtonsRepostSourceFragmentDoc,
  CurrentUserPostVoteStatus,
  type FeedPostActionsInfoFragment,
  FeedPostActionsInfoFragmentDoc,
  PostVoteChange,
  RepostDocument,
  UndoRepostDocument,
} from '@graphql/reactQuery';
import {
  type FragmentType,
  getFragment,
  useMutation,
  VoteOnPostDocument,
} from '@graphql/reactQuery';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { useLogError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, FEATURE_GATES } from '@utils/constants';
import { exhaustiveCheck, focusHtmlElement, getPostInputId } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { CommentButton } from './CommentButton';
import { RepostButton } from './RepostButton';
import { SharePostButton } from './SharePostButton';
import { UpvoteButton } from './UpvoteButton';
import { usePostStatus } from './usePostStatus';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation VoteOnPost($input: SetVoteOnPostInput!) {
    voteOnPostRL(input: $input) {
      __typename
      ... on RateLimitError {
        message
      }
    }
  }

  mutation Repost($input: MutationRepostInput!) {
    repost(input: $input) {
      __typename
      ... on MutationRepostSuccess {
        data {
          __typename
          id
        }
      }
      ... on Error {
        message
      }
    }
  }

  mutation UndoRepost($postId: UUID!) {
    undoRepost(postId: $postId) {
      __typename
      id
    }
  }

  fragment ActionButtonsRepostSource on Repost {
    id
    creator {
      id
      publicAddress
    }
  }

  fragment FeedPostActionsInfo on Post {
    __typename
    id
    commentCount
    upvoteCount
    repostCount
    webappUri
    ...SharePostInfo
    ...FeedPostCommentModal
  }
`);

export enum CommentButtonAction {
  ShowModal,
  FocusInput,
}

export function isRepostablePost(post: Pick<FeedPostActionsInfoFragment, '__typename'>) {
  switch (post.__typename) {
    case 'PlaylistPost':
    case 'ReleasePost': {
      return true;
    }
    default: {
      return false;
    }
  }
}

export function PostActionButtons({
  postFrag,
  repostSource,
  commentButtonAction,
  sourceLocation,
  resourceOwnerTwitterHandle,
  hideShareButton,
  buttonClassName,
  containerClassName,
  iconSize,
  source,
  hideRepostButton,
}: {
  postFrag: FragmentType<FeedPostActionsInfoFragmentDoc>;
  repostSource: FragmentType<ActionButtonsRepostSourceFragmentDoc> | null;
  commentButtonAction: CommentButtonAction;
  sourceLocation: ShareModalSource;
  resourceOwnerTwitterHandle: string | null;
  hideShareButton: boolean;
  hideRepostButton: boolean;
  buttonClassName?: string;
  containerClassName?: string;
  iconSize: number;
  source: PostSource;
}) {
  const post = getFragment(FeedPostActionsInfoFragmentDoc, postFrag);
  const repostInfo = getFragment(ActionButtonsRepostSourceFragmentDoc, repostSource);

  const { id: postId, commentCount } = post;

  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();
  const hydrated = useIsHydrated();
  const { openModal } = useModal();

  const { openToast } = useToast();
  const logError = useLogError();

  const { postStatus, setPostStatus } = usePostStatus({
    postId,
  });

  const onCommentButtonClick = useStableCallback(() => {
    trackClickEvent({
      event: 'Comment Icon Click',
      properties: {
        associationType: 'Post',
        associationId: postId,
        sourceComponent: source,
        repostId: repostInfo?.id ?? null,
        repostUserId: repostInfo?.creator.id ?? null,
        repostUserPublicAddress: repostInfo?.creator.publicAddress ?? null,
      },
    });
    if (!isAuthenticatedWithAPI) {
      login();
      return;
    }
    if (commentButtonAction === CommentButtonAction.ShowModal) {
      openModal(ModalType.FEED_POST_COMMENT, {
        body: <FeedPostReplyModal key={`feed-post-comment-modal-${post.id}`} postFrag={post} />,
      });
    } else {
      focusHtmlElement(getPostInputId(post.id));
    }
  });

  const { mutateAsync: voteOnPost, isLoading: isVoting } = useMutation(VoteOnPostDocument, {});

  const allowReposts = isRepostablePost(post);

  const { mutateAsync: repost, isLoading: isCreatingRepost } = useMutation(RepostDocument, {});
  const { mutateAsync: undoRepost, isLoading: isUndoingRepost } = useMutation(
    UndoRepostDocument,
    {},
  );

  const isReposting = isCreatingRepost || isUndoingRepost;

  const handleUpvoteClicked = useStableCallback(async () => {
    trackClickEvent({
      event: postStatus?.voteStatus === 'UPVOTED' ? 'Undo Upvote Clicked' : 'Upvote Clicked',
      properties: {
        associationType: 'Post',
        associationId: postId,
        sourceComponent: source,
        repostId: repostInfo?.id ?? null,
        repostUserId: repostInfo?.creator.id ?? null,
        repostUserPublicAddress: repostInfo?.creator.publicAddress ?? null,
      },
    });

    if (!isAuthenticatedWithAPI) {
      login();
      return;
    }

    if (isVoting || !postStatus) return;
    const { voteStatus, upvoteCount, repostCount, repostStatus } = postStatus;

    try {
      let mutateResponse: Awaited<ReturnType<typeof voteOnPost>> | undefined;
      if (voteStatus === CurrentUserPostVoteStatus.NoVote) {
        setPostStatus({
          postId,
          voteStatus: CurrentUserPostVoteStatus.Upvoted,
          upvoteCount: upvoteCount + 1,
          repostCount,
          repostStatus,
        });
        mutateResponse = await voteOnPost({ input: { postId, voteStatus: PostVoteChange.Upvote } });
      } else if (voteStatus === CurrentUserPostVoteStatus.Upvoted) {
        setPostStatus({
          postId,
          voteStatus: CurrentUserPostVoteStatus.NoVote,
          upvoteCount: Math.max(upvoteCount - 1, 0),
          repostCount,
          repostStatus,
        });
        mutateResponse = await voteOnPost({
          input: { postId, voteStatus: PostVoteChange.RemoveVote },
        });
      }

      if (mutateResponse?.data.voteOnPostRL?.__typename === 'RateLimitError') {
        const error = new Error(mutateResponse.data.voteOnPostRL.message);

        logError({
          action: POSTING_ERROR_ACTIONS.UPVOTING_POST_FAILED,
          error,
          errorType: ERROR_TYPE.RATE_LIMIT,
          isArtistOnlyPage: false,
          level: 'warning',
          message: mutateResponse.data.voteOnPostRL.message,
          pillar: 'POSTING',
          toast: 'Hit limit for upvoting posts',
          indexedTags: {
            postId,
          },
        });
      }
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.UPVOTING_POST_FAILED,
        error,
        errorType: ERROR_TYPE.UNKNOWN,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'There was an error upvoting a post',
        pillar: 'POSTING',
        toast: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER,
        indexedTags: {
          postId,
        },
      });
    }
  });

  const handleRepostClicked = useStableCallback(async () => {
    trackClickEvent({
      event: postStatus?.repostStatus === 'REPOSTED' ? 'Undo Repost Clicked' : 'Repost Clicked',
      properties: {
        associationType: 'Post',
        associationId: postId,
        sourceComponent: source,
        repostId: repostInfo?.id ?? null,
        repostUserId: repostInfo?.creator.id ?? null,
        repostUserPublicAddress: repostInfo?.creator.publicAddress ?? null,
      },
    });
    if (!isAuthenticatedWithAPI) {
      login();
      return;
    }

    if (isReposting || !postStatus) return;

    const { voteStatus, upvoteCount, repostCount, repostStatus } = postStatus;

    if (repostStatus === 'NOT_REPOSTED') {
      setPostStatus({
        postId,
        voteStatus,
        upvoteCount,
        repostCount: repostCount + 1,
        repostStatus: 'REPOSTED',
      });
      await repost({ input: { postId } });

      openToast({
        variant: 'success',
        text: (
          <p>
            Reposted.{' '}
            <AccessibleLink
              pathnameUri={webappUriPath(post.webappUri)}
              className="font-semibold underline"
            >
              View post
            </AccessibleLink>
          </p>
        ),
      });
    } else if (repostStatus === 'REPOSTED') {
      setPostStatus({
        postId,
        voteStatus,
        upvoteCount,
        repostCount: Math.max(repostCount - 1, 0),
        repostStatus: 'NOT_REPOSTED',
      });
      await undoRepost({
        postId,
      });
    }
  });

  /**
   * Only use the status upvote count if the user is logged in
   */
  const upvoteCount = useMemo(() => {
    if (isAuthenticatedWithAPI && postStatus) {
      return postStatus.upvoteCount;
    } else {
      return post.upvoteCount;
    }
  }, [isAuthenticatedWithAPI, postStatus, post.upvoteCount]);

  /**
   * Only use the status repost count if the user is logged in
   */
  const repostCount = useMemo(() => {
    if (isAuthenticatedWithAPI && postStatus) {
      return postStatus.repostCount;
    } else {
      return post.repostCount;
    }
  }, [isAuthenticatedWithAPI, postStatus, post.repostCount]);

  const isLoading = useMemo(() => {
    if (isAuthenticatedWithAPI) {
      return !Boolean(postStatus);
    } else {
      return !hydrated;
    }
  }, [hydrated, isAuthenticatedWithAPI, postStatus]);

  const { value: repostsEnabledFF, isLoading: repostsFFLoading } = useGate(FEATURE_GATES.REPOSTS);

  const onShareClick = useStableCallback(() => {
    trackClickEvent({
      event: 'Post Share Clicked',
      properties: {
        associationType: 'Post',
        associationId: postId,
        sourceComponent: source,
        repostId: repostInfo?.id ?? null,
        repostUserId: repostInfo?.creator.id ?? null,
        repostUserPublicAddress: repostInfo?.creator.publicAddress ?? null,
      },
    });
  });

  if (isLoading || (repostsFFLoading && source === 'Post Page'))
    return <div className="-my-1 h-7 w-36 animate-pulse rounded-md bg-neutral200" />;

  const repostsEnabled = repostsEnabledFF && allowReposts;

  const componentType: 'No Counts' | 'With Counts' = repostsEnabled
    ? source === 'Post Page'
      ? 'No Counts'
      : 'With Counts'
    : 'With Counts';

  switch (componentType) {
    case 'No Counts': {
      return (
        <div className={twMerge('flex flex-row justify-between', containerClassName)}>
          {/**
           * Upvote button
           */}
          <UpvoteButton
            disabled={isVoting}
            onClick={handleUpvoteClicked}
            upvoteCount={null}
            voteStatus={postStatus?.voteStatus}
            iconSize={20}
            className={buttonClassName}
          />

          {/**
           * Repost button
           */}
          {!!repostsEnabled && !hideRepostButton && (
            <RepostButton
              disabled={isReposting}
              className={twMerge(buttonClassName)}
              onClick={handleRepostClicked}
              repostCount={null}
              repostStatus={postStatus?.repostStatus}
              iconSize={20}
            />
          )}

          {/**
           * Comment button
           */}
          <CommentButton
            onClick={onCommentButtonClick}
            commentCount={null}
            variant="post"
            className={twMerge(buttonClassName)}
            iconSize={20}
          />

          {!hideShareButton && (
            <SharePostButton
              post={post}
              iconSize={20}
              sourceLocation={sourceLocation}
              releasePostArtistTwitterHandle={resourceOwnerTwitterHandle}
              onClick={onShareClick}
            />
          )}
        </div>
      );
    }
    case 'With Counts': {
      return (
        <div
          className={twMerge('-mx-2 -my-1 flex h-7 items-center gap-3', containerClassName)}
          data-feed-clickable-div
        >
          {/**
           * Upvote button
           */}
          <UpvoteButton
            disabled={isVoting}
            onClick={handleUpvoteClicked}
            upvoteCount={upvoteCount}
            voteStatus={postStatus?.voteStatus}
            iconSize={iconSize}
            className={buttonClassName}
          />

          {/**
           * Repost button
           */}
          {!!repostsEnabled && !hideRepostButton && (
            <RepostButton
              disabled={isReposting}
              className={buttonClassName}
              onClick={handleRepostClicked}
              repostCount={repostCount}
              repostStatus={postStatus?.repostStatus}
              iconSize={iconSize}
            />
          )}

          {/**
           * Comment button
           */}
          <CommentButton
            onClick={onCommentButtonClick}
            commentCount={commentCount}
            variant="post"
            className={buttonClassName}
            iconSize={iconSize}
          />

          {!hideShareButton && (
            <SharePostButton
              post={post}
              sourceLocation={sourceLocation}
              releasePostArtistTwitterHandle={resourceOwnerTwitterHandle}
              /**
               * This icon is intentionally different size since it doesn't have a count
               * and it's shape make it look better at a smaller size
               */
              iconSize={16}
              onClick={onShareClick}
            />
          )}
        </div>
      );
    }
    default: {
      exhaustiveCheck(componentType);

      return null;
    }
  }
}
